import React from "react";
import PropTypes from "prop-types";

import { isSubscriptionMember, isClassicLens, isActiveMembership } from "helpers/membership-helpers";
import RtrTabs from "components/design-system/rtr-tabs";
import ClothesClassicIcon from "images/clothes-classic.svg";
import ClothesSubIcon from "images/clothes-sub.svg";
import HeapHelpers from "helpers/heap-helpers";
import { membershipLens } from "rtr-constants";
import SubscriptionPromoBanner from "../shared/subscription-promo-banner";
import { useSelector } from "react-redux";
import ReservePromoBanner from "../shared/reserve-promo-banner";

const PdpAddToBagTabs = ({
  userData = {},
  product = {},
  reservation = {},
  handleWayfindCtaClick = () => {},
  renderMembershipFormFields = () => {},
  renderRentableFormFields = () => {},
}) => {
  const cmsPromoBanner = useSelector(state => state.smartComponentState?.PDPContent?.data?.membership);
  const activeMember = useSelector(state => isActiveMembership(state.membershipState));

  const tabReserveButton = (
    <div className="flex flex-col">
      <span>Reserve in Advance</span>
      <span className="text-sm mt-0.5">
        {isSubscriptionMember(userData) ? (
          <span className={isClassicLens(userData) ? "text-tealGreen" : ""}>Members get 25% off</span>
        ) : (
          "No subscription required"
        )}
      </span>
    </div>
  );

  const tabMembershipButton = (
    <div className="flex flex-col">
      <span>Subscription</span>
      <span className="text-sm mt-1">
        {isSubscriptionMember(userData) ? (
          "Rent or buy"
        ) : (
          <span className={!isClassicLens(userData) ? "text-tealGreen" : ""}>
            {cmsPromoBanner?.subscriptionTabSubText || ""}
          </span>
        )}
      </span>
    </div>
  );

  const tabReserveContent = (
    <>
      <ReservePromoBanner />
      <div className="flex flex-col gap-2">
        <div className="flex gap-1">
          {!reservation?.duration && "Starting from"}
          {`$${product?.price?.adjusted}`}
          {product?.price?.adjusted !== product?.price?.base && (
            <span className="text-lightGray line-through">${product?.price?.base}</span>
          )}
          {reservation?.duration && (
            <div className="text-darkGray flex gap-1 items-center">
              |<span>{reservation?.duration}-day rental</span>
            </div>
          )}
        </div>
        <div className="flex justify-between items-center mb-4">
          <div className="flex flex-col text-sm">
            {isSubscriptionMember(userData) ? (
              <span className="text-tealGreen">Members get 25% off</span>
            ) : (
              <span>No subscription required</span>
            )}

            <span>Includes a free backup size </span>
          </div>
          <ClothesClassicIcon />
        </div>
        {renderRentableFormFields()}
      </div>
    </>
  );

  const heapTabClickEvent = tabId => {
    HeapHelpers.fireHeapEvent("tabbed_lens_switch_on_pdp", { lensTabClicked: tabId });
  };

  const tabMembershipContent = (
    <div className="mb-4">
      {!activeMember ? (
        <>
          <SubscriptionPromoBanner />

          <div className="flex flex-col w-full mb-4">
            <div className="flex flex-row justify-between mb-2 items-end">
              <p>{cmsPromoBanner?.pricingCopy || ""}</p>
              <p className="text-sm text-darkGray">Most Popular Plan</p>
            </div>
            <div className="flex flex-row justify-between">
              <div className="flex flex-col">
                <p className="text-sm text-darkGray">5 items per shipment</p>
                <p className="text-sm text-darkGray">2 shipments per month</p>
              </div>
              <ClothesSubIcon />
            </div>
          </div>
        </>
      ) : null}
      {renderMembershipFormFields()}
    </div>
  );

  const tabData = [
    {
      id: "Subscription",
      additionalCallback: () => {
        if (isClassicLens(userData)) {
          heapTabClickEvent("Subscription");
          handleWayfindCtaClick();
        }
      },
      label: tabMembershipButton,
      content: tabMembershipContent,
    },
    {
      id: "Reserve",
      additionalCallback: () => {
        if (!isClassicLens(userData)) {
          heapTabClickEvent("Reserve");
          handleWayfindCtaClick(membershipLens.classic);
        }
      },
      label: tabReserveButton,
      content: tabReserveContent,
    },
  ];

  return (
    <RtrTabs
      className="-mx-4 sm:-mx-8 text-onyx"
      tabs={tabData}
      activeTabId={isClassicLens(userData) ? "Reserve" : "Subscription"}
      dataTestId="pdp-add-to-bag-tabs"
    />
  );
};

PdpAddToBagTabs.propTypes = {
  userData: PropTypes.object,
  product: PropTypes.object,
  handleWayfindCtaClick: PropTypes.func,
  reservation: PropTypes.object,
  renderMembershipFormFields: PropTypes.func,
  renderRentableFormFields: PropTypes.func,
};

export default PdpAddToBagTabs;
